import React, { useEffect, useState } from "react";
import api, { brl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";

const useCatalogo = () => {
  const [cat, setCat] = useState([]);
  useEffect(() => { api.get("/planos/recursos-catalogo").then((r) => setCat(r.data.catalogo)).catch(() => {}); }, []);
  return cat;
};

const Lista = ({ cat, marcados, onToggle, base = [], testid }) => (
  <div className="grid sm:grid-cols-2 gap-1.5" data-testid={testid}>
    {cat.map((r) => {
      const on = marcados.includes(r.chave), padrao = base.includes(r.chave);
      return (
        <label key={r.chave} className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs ${on ? "bg-emerald-50 border-emerald-200" : "bg-white"}`}>
          <input type="checkbox" className="mt-0.5" checked={on} onChange={() => onToggle(r.chave)} data-testid={`${testid}-${r.chave}`} />
          <span className="min-w-0"><span className="font-medium">{r.label}</span>
            {r.basico && <span className="text-slate-400"> · básico</span>}
            {base.length > 0 && on !== padrao && <Badge className="ml-1 bg-amber-100 text-amber-800 text-[10px]">{on ? "incluído p/ esta empresa" : "retirado p/ esta empresa"}</Badge>}
          </span>
        </label>
      );
    })}
  </div>
);

/** Master: benefícios padrão de um plano, com prévia do impacto nas empresas que seguem o padrão. */
export const PlanoRecursosDialog = ({ plano, onClose, onSaved }) => {
  const cat = useCatalogo();
  const { refresh } = useAuth();
  const [sel, setSel] = useState(plano?.recursos || []);
  const [imp, setImp] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (plano) { setSel(plano.recursos || []); api.get(`/planos/${plano.id}/impacto`).then((r) => setImp(r.data)).catch(() => setImp(null)); } }, [plano]);
  if (!plano) return null;
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const salvar = async () => {
    setBusy(true);
    try { await api.put(`/planos/${plano.id}/recursos`, { recursos: sel }); toast.success("Benefícios do plano atualizados"); onSaved(); onClose(); refresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plano-recursos-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 gold-text" /> Benefícios do plano {plano.nome}</DialogTitle>
          <DialogDescription>Plano de assinatura do software (não altera condições dos empréstimos). Mudanças valem para as empresas que seguem o padrão; exceções individuais são preservadas.</DialogDescription></DialogHeader>
        <Lista cat={cat} marcados={sel} onToggle={toggle} testid="plano-rec" />
        {imp && (
          <div className="rounded-lg border bg-amber-50/60 p-3 text-xs space-y-1" data-testid="plano-impacto">
            <div><b>Impacto:</b> {imp.seguem_padrao.length} empresa(s) seguem o padrão e receberão esta alteração{imp.seguem_padrao.length > 0 && <>: {imp.seguem_padrao.map((e) => e.nome).join(", ")}</>}.</div>
            <div>{imp.com_excecoes.length} empresa(s) com exceções individuais (preservadas){imp.com_excecoes.length > 0 && <>: {imp.com_excecoes.map((e) => e.nome).join(", ")}</>}.</div>
            <div className="text-slate-500">Cobranças já registradas não são alteradas.</div>
          </div>
        )}
        <DialogFooter className="gap-2"><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={salvar} disabled={busy} className="bg-onyx text-white" data-testid="plano-rec-salvar">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar benefícios"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Master: plano contratado, benefícios e preço efetivos de UMA empresa, com exceções individuais. */
export const EmpresaPlanoEfetivo = ({ empresa, onChanged }) => {
  const cat = useCatalogo();
  const { refresh } = useAuth();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState([]);
  const [preco, setPreco] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/empresas/${empresa.id}/plano-efetivo`).then((r) => { setD(r.data); setSel(r.data.recursos_efetivos); setPreco(r.data.excecoes.preco ?? ""); }).catch(() => setD(null));
  useEffect(() => { load(); }, [empresa.id, empresa.plano_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d || !d.plano) return null;
  const base = d.plano.recursos;
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const salvar = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/empresas/${empresa.id}/plano-excecoes`, { preco: preco === "" ? null : Number(preco), recursos_add: sel.filter((k) => !base.includes(k)), recursos_remove: base.filter((k) => !sel.includes(k)) });
      setD(data); setSel(data.recursos_efetivos); toast.success("Personalização salva só para esta empresa"); onChanged?.(); refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };
  const restaurar = async () => {
    setBusy(true);
    try { const { data } = await api.delete(`/empresas/${empresa.id}/plano-excecoes`); setD(data); setSel(data.recursos_efetivos); setPreco(""); toast.success("Padrão do plano restaurado"); onChanged?.(); refresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="empresa-plano-efetivo">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div><b>Plano contratado:</b> {d.plano.nome} <span className="text-slate-500">(padrão {brl(d.plano.preco)}/mês)</span></div>
        <div data-testid="preco-efetivo"><b>Preço efetivo:</b> <span className="mono gold-text font-bold">{brl(d.preco_efetivo)}/mês</span>{d.tem_excecoes && <Badge className="ml-2 bg-amber-100 text-amber-800" data-testid="badge-excecoes">com exceções</Badge>}</div>
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div><div className="text-xs text-slate-500">Preço específico desta empresa (R$/mês) — vazio = padrão do plano</div><Input type="number" step="0.01" min="0" value={preco} onChange={(e) => setPreco(e.target.value)} className="w-44" data-testid="preco-especifico" /></div>
      </div>
      <div className="text-xs text-slate-500">Benefícios efetivos ({sel.length}): marque/desmarque para incluir ou retirar só nesta empresa.</div>
      <Lista cat={cat} marcados={sel} onToggle={toggle} base={base} testid="emp-rec" />
      <div className="flex gap-2 flex-wrap justify-end">
        <Button size="sm" variant="outline" onClick={restaurar} disabled={busy || !d.tem_excecoes} data-testid="restaurar-padrao-btn">Restaurar padrão do plano</Button>
        <Button size="sm" onClick={salvar} disabled={busy} className="bg-onyx text-white" data-testid="salvar-excecoes-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar exceções"}</Button>
      </div>
    </div>
  );
};
